import React from "react";
import { Clapperboard, UserRound, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { generateProjectShowColour, getReadableTextColour, normalizeHexColour } from "../lib/projectShowColours";

type CastDisplayItem = {
  characterId: string;
  characterName: string;
  actorName: string;
  actorImageUrl?: string | null;
};

type HeroCastSlot =
  | (CastDisplayItem & { isPlaceholder?: false })
  | {
      characterId: string;
      characterName: string;
      actorName: string;
      actorImageUrl?: string | null;
      isPlaceholder: true;
    };

type TeamDisplayItem = {
  id: string;
  displayGroup: "leadership" | "leads" | "team";
  name: string;
  role: string;
  avatarUrl?: string | null;
};

type CrewDisplayItem = {
  id: string;
  role: string;
  name: string;
};

type DisplayPayload = {
  project: {
    id: string;
    projectName: string;
    logline?: string | null;
    heroImageUrl?: string | null;
    themeAccentColor?: string | null;
  };
  cast: CastDisplayItem[];
  team: TeamDisplayItem[];
  crew: CrewDisplayItem[];
};

const showDashboardDisplayCache = new Map<string, DisplayPayload>();

interface ProjectShowDashboardProps {
  projectId: string | null;
  projectName: string;
  productionCompanyName?: string | null;
  fallbackImageUrl?: string | null;
  fallbackAccentColor?: string | null;
}

const TEAM_GROUPS: Array<{ id: TeamDisplayItem["displayGroup"]; label: string }> = [
  { id: "leadership", label: "Leadership" },
  { id: "leads", label: "Leads" },
  { id: "team", label: "Team" },
];

function initials(name: string) {
  return String(name || "TBC")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "T";
}

const HERO_CAST_SLOTS = 8;
const TABLET_HERO_CAST_SLOTS = 6;

function buildHeroCastSlots(cast: CastDisplayItem[], count: number): HeroCastSlot[] {
  const slots: HeroCastSlot[] = cast.slice(0, count);
  for (let index = slots.length; index < count; index += 1) {
    slots.push({
      characterId: `placeholder-${count}-${index}`,
      characterName: "Character TBC",
      actorName: "Actor TBC",
      actorImageUrl: null,
      isPlaceholder: true,
    });
  }
  return slots;
}

function HeroCastCard({ slot, isLoading = false }: { slot: HeroCastSlot; isLoading?: boolean }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-white/14 bg-white/12 shadow-[0_14px_34px_rgba(15,23,42,0.22)] backdrop-blur-md ${
        slot.isPlaceholder ? "bg-white/10" : ""
      } ${
        isLoading && slot.isPlaceholder ? "animate-pulse" : ""
      }`}
    >
      <div className="aspect-[5/4] bg-white/10">
        {slot.actorImageUrl && !slot.isPlaceholder ? (
          <img
            src={slot.actorImageUrl}
            alt={slot.actorName || slot.characterName}
            className="h-full w-full object-cover object-center"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_50%_28%,rgba(255,255,255,0.26),transparent_20%),linear-gradient(180deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.08)_100%)] text-white/80">
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/18 bg-white/10">
              <UserRound className="h-7 w-7" />
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1 px-3 py-2.5">
        <p className="truncate text-sm font-semibold text-white">{slot.characterName}</p>
        <p className="truncate text-xs text-white/70">{slot.actorName || "Actor TBC"}</p>
      </div>
    </div>
  );
}

export function ProjectShowDashboard({ projectId, projectName, productionCompanyName, fallbackImageUrl, fallbackAccentColor }: ProjectShowDashboardProps) {
  const { user } = useAuth();
  const [data, setData] = React.useState<DisplayPayload | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!user || !projectId) return;
    let cancelled = false;
    setError(null);
    const cachedPayload = showDashboardDisplayCache.get(projectId);
    if (cachedPayload) {
      setData(cachedPayload);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    fetch(`/api/projects/${projectId}/show-dashboard?view=display`, {
      headers: { "x-user-uid": user.uid },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "Failed to load show dashboard.");
        showDashboardDisplayCache.set(projectId, payload);
        if (!cancelled) setData(payload);
      })
      .catch((fetchError: Error) => {
        if (!cancelled) setError(fetchError.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, user]);

  const project = data?.project;
  const accentColor = normalizeHexColour(project?.themeAccentColor || fallbackAccentColor) || generateProjectShowColour(projectId || projectName || "show");
  const accentTextColor = getReadableTextColour(accentColor);
  const heroImage = project?.heroImageUrl || fallbackImageUrl || "";
  const displayProjectName = project?.projectName || projectName || "Untitled Project";
  const displayProductionCompany = String(productionCompanyName || "").trim();
  const cast = data?.cast || [];
  const heroCast = cast.slice(0, HERO_CAST_SLOTS);
  const desktopHeroCastSlots = buildHeroCastSlots(heroCast, HERO_CAST_SLOTS);
  const tabletHeroCastSlots = buildHeroCastSlots(heroCast, TABLET_HERO_CAST_SLOTS);
  const mobileHeroCastSlots = buildHeroCastSlots(heroCast, HERO_CAST_SLOTS);
  const castStatusMessage = isLoading
    ? "Loading cast…"
    : heroCast.length === 0
      ? "No cast links yet. Use Project Settings → Cast to link actors."
      : "Cast shown in shooting schedule order.";
  const team = data?.team || [];
  const crew = data?.crew || [];
  const heroLayoutStyle = {
    backgroundColor: accentColor,
    ["--hero-image-width" as string]: "clamp(24rem, 45vw, 42rem)",
    ["--hero-fade-width" as string]: "clamp(6rem, 11vw, 10rem)",
  } as React.CSSProperties;

  if (!projectId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Save the project before opening the show dashboard.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center text-red-700 shadow-sm dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm dark:border-slate-800" style={heroLayoutStyle}>
        <div className="relative min-h-[420px] sm:min-h-[470px] lg:min-h-[clamp(31rem,44vw,36rem)]">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 22% 18%, rgba(255,255,255,0.12), transparent 22%), radial-gradient(circle at 74% 56%, rgba(255,255,255,0.08), transparent 20%), linear-gradient(135deg, ${accentColor} 0%, color-mix(in srgb, ${accentColor} 88%, #111827) 100%)`,
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(180deg,rgba(2,6,23,0)_0%,rgba(2,6,23,0.16)_100%)]" />

          <div className="relative z-10 flex min-h-[420px] flex-col px-6 py-6 sm:min-h-[470px] sm:px-8 sm:py-8 lg:min-h-[clamp(31rem,44vw,36rem)] lg:px-10 lg:py-10">
            <div className="flex-1 lg:hidden">
              <div className="relative overflow-hidden rounded-[1.25rem] border border-white/12 bg-white/8 shadow-[0_22px_56px_rgba(15,23,42,0.28)]">
                {heroImage ? (
                  <>
                    <img
                      src={heroImage}
                      alt={displayProjectName}
                      className="aspect-square w-full object-cover object-[center_18%]"
                      referrerPolicy="no-referrer"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.12)_0%,rgba(2,6,23,0.24)_24%,rgba(2,6,23,0.56)_62%,rgba(2,6,23,0.82)_100%)]" />
                  </>
                ) : (
                  <div className="aspect-square w-full bg-[linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))]" />
                )}
                <div className="absolute inset-x-0 top-0 p-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/80 backdrop-blur">
                    <Clapperboard className="h-3.5 w-3.5" />
                    <span>Show Dashboard</span>
                    {displayProductionCompany ? <span className="text-white/55">•</span> : null}
                    {displayProductionCompany ? <span className="max-w-[180px] truncate sm:max-w-none">{displayProductionCompany}</span> : null}
                  </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="w-full">
                    <h1 className="max-w-full font-brandon text-[clamp(2.8rem,10vw,4.2rem)] font-medium leading-[0.92] text-white">
                      {displayProjectName}
                    </h1>
                    {project?.logline ? (
                      <p className="mt-4 max-w-full text-base leading-7 text-white/80 sm:text-lg">{project.logline}</p>
                    ) : (
                      <p className="mt-4 max-w-full text-base leading-7 text-white/58 sm:text-lg">Add a logline in Project Settings to give this show page a sharper editorial hook.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 min-h-[17.5rem]">
                <div className="mb-3 flex items-center justify-between gap-4 text-white">
                  <h2 className="font-brandon text-xl font-medium">Cast</h2>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/75 backdrop-blur">{heroCast.length} linked</span>
                </div>
                <div className="-mx-1 overflow-x-auto pb-2">
                  <div className="flex min-w-max gap-3 px-1">
                    {mobileHeroCastSlots.map((slot) => (
                      <div key={slot.characterId} className="w-[clamp(9rem,42vw,11rem)] shrink-0">
                        <HeroCastCard slot={slot} isLoading={isLoading} />
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 min-h-[1.25rem] px-1 text-sm text-white/68">{castStatusMessage}</p>
                </div>
              </div>
            </div>

            <div className="hidden lg:grid lg:flex-1 lg:grid-cols-[minmax(24rem,31rem)_minmax(0,1fr)] lg:items-center lg:gap-8">
              <div className="flex h-full items-center">
                <div className="relative aspect-square w-full max-w-[clamp(27rem,31vw,31rem)] overflow-hidden rounded-[1rem] border border-white/12 bg-white/8 shadow-[0_28px_72px_rgba(15,23,42,0.30)]">
                  {heroImage ? (
                    <>
                      <img
                        src={heroImage}
                        alt={displayProjectName}
                        className="h-full w-full object-cover object-[center_22%]"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(2,6,23,0.12)_18%,rgba(2,6,23,0.3)_52%,rgba(2,6,23,0.82)_100%)]" />
                      <div className="absolute inset-x-0 inset-y-0 bg-[linear-gradient(90deg,rgba(2,6,23,0)_56%,rgba(2,6,23,0.18)_78%,rgba(2,6,23,0.36)_100%)]" />
                    </>
                  ) : (
                    <div className="h-full w-full bg-[linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))]" />
                  )}
                  <div className="absolute inset-x-0 top-0 p-6">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/80 backdrop-blur">
                      <Clapperboard className="h-3.5 w-3.5" />
                      <span>Show Dashboard</span>
                      {displayProductionCompany ? <span className="text-white/55">•</span> : null}
                      {displayProductionCompany ? <span className="max-w-[180px] truncate">{displayProductionCompany}</span> : null}
                    </div>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <div className="w-full">
                      <h1 className="max-w-full font-brandon text-[clamp(2.7rem,4.35vw,4.5rem)] font-medium leading-[0.92] text-white">
                        {displayProjectName}
                      </h1>
                      {project?.logline ? (
                        <p className="mt-4 max-w-full text-base leading-7 text-white/80">{project.logline}</p>
                      ) : (
                        <p className="mt-4 max-w-full text-base leading-7 text-white/58">Add a logline in Project Settings to give this show page a sharper editorial hook.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex h-full items-center justify-end">
                <div className="w-full max-w-[48rem] min-h-[23rem]">
                  <div className="mb-4 flex items-center justify-between gap-4 text-white">
                    <h2 className="font-brandon text-2xl font-medium">Cast</h2>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/75 backdrop-blur">{heroCast.length} linked</span>
                  </div>
                  <>
                    <div className="hidden xl:grid xl:auto-rows-fr xl:grid-cols-4 xl:gap-3">
                      {desktopHeroCastSlots.map((slot) => (
                        <HeroCastCard key={slot.characterId} slot={slot} isLoading={isLoading} />
                      ))}
                    </div>
                    <div className="hidden md:grid md:auto-rows-fr md:grid-cols-3 md:gap-3 xl:hidden">
                      {tabletHeroCastSlots.map((slot) => (
                        <HeroCastCard key={slot.characterId} slot={slot} isLoading={isLoading} />
                      ))}
                    </div>
                    <p className="mt-3 min-h-[1.25rem] max-w-sm text-sm text-white/68">{castStatusMessage}</p>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-brandon text-2xl font-medium text-slate-900 dark:text-slate-100">Internal Team</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">People / HR links only. Display cards use name, role, and approved avatar.</p>
            </div>
          </div>

          <div className="mt-6 space-y-6">
            {TEAM_GROUPS.map((group) => {
              const members = team.filter((member) => member.displayGroup === group.id);
              return (
                <div key={group.id}>
                  <div className="mb-3 flex items-center gap-3">
                    <h3 className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">{group.label}</h3>
                    <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                    <span className="text-xs font-semibold text-slate-400">{members.length}</span>
                  </div>
                  {members.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60">
                          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                            {member.avatarUrl ? (
                              <img src={member.avatarUrl} alt={member.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-sm font-bold">{initials(member.name)}</div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{member.name}</p>
                            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{member.role}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      No {group.label.toLowerCase()} assigned yet.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="font-brandon text-2xl font-medium text-slate-900 dark:text-slate-100">Crew</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">CRM / External People links only.</p>
          <div className="mt-5 space-y-2">
            {crew.length > 0 ? (
              crew.map((member) => (
                <div key={member.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/60">
                  <span className="font-semibold text-slate-900 dark:text-slate-100">{member.role}</span>
                  <span className="text-slate-400"> - </span>
                  <span className="text-slate-600 dark:text-slate-300">{member.name}</span>
                </div>
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                No external crew linked yet. Add crew in Project Settings → Crew.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
